import Paginator from '@/models/Paginator'
import Task from '@/models/Task'
import { common as pathLoader } from '@/api'
import { task as backend } from '@/api'
import { find, findIndex, sortBy } from 'lodash'

export default {

  namespaced: true,

  state: {
    tasks: new Paginator,
    task: new Task
  },

  mutations: {
    clear: (state) => state.task = new Task,
    clearTasks: (state) => state.tasks = new Paginator,
    setTasks: (state, payload) => state.tasks = payload,
    setTask: (state, payload) => {
      state.task = payload
      state.task.estimated_time = payload.estimated_display_time
    },
    name: (state, payload) => state.task.name = payload,
    description: (state, payload) => state.task.description = payload,
    estimatedTime: (state, payload) => state.task.estimated_time = payload,
    project: (state, payload) => state.task.project_id = payload,
    updateLabels: (state, payload) => state.task.labels = payload,
    updateAssignees: (state, payload) => state.task.assignees = payload,
    updatePriority: (state, payload) => state.task.priority_id = payload,
    updateAsset: (state, payload) => state.task.asset_id = payload,
    updateMilestone: (state, payload) => state.task.milestone_id = payload,
    addComment: (state, payload) => state.task.comments.push(payload),
    trashComment: (state, payload) => {
      let comment = find(state.task.comments, comment => comment.id === payload.id)
      comment.deleted_at = payload.deleted_at
    },
    updateComment: (state, payload) => {
      let comment = find(state.task.comments, comment => comment.id === payload.id)
      comment.content = payload.content
    },
    updateCommentReactionCounts: (state, payload) => {
      let comment = find(state.task.comments, ['id', payload.id])
      comment.reaction_counts = payload.reaction_counts
    },
    addTodo: (state, payload) => {
      state.task.todos.push(payload)
    },
    setStatus: (state, { completed_at, status }) => {
      state.task.completed_at = completed_at
      state.task.status = status
    },
    assignUser: (state, payload) => state.task.assignees.push(payload),
    assignLabel: (state, payload) => state.task.labels.push(payload),
    unassignUser: (state, user) => {
      let index = state.task.assignees.indexOf(user)
      state.task.assignees.splice(index, 1)
    },
    removeLabel: (state, label) => {
      let index = state.task.labels.indexOf(label)
      state.task.labels.splice(index, 1)
    },
    spendTime: (state, payload) => state.task.time_spent.push(payload),
    removeTodo: (state, todoId) => {
      let index = findIndex(state.task.todos, todo => todo.id === todoId)
      state.task.todos.splice(index, 1)
    },
    toggleTodoComplete: (state, todoId) => {
      let index = findIndex(state.task.todos, todo => todo.id === todoId)

      let todo = state.task.todos[index]
      if(todo.completed) {
        todo.completed = null
      } else {
        todo.completed = new Date
      }

      state.task.todos.splice(index, 1, todo)
    },
    updateTodoContent: (state, payload) => {
      let index = findIndex(state.task.todos, todo => todo.id === payload.id)
      let todo = state.task.todos[index]
      todo.content = payload.content
      state.task.todos.splice(index, 1, todo)
    },
    removeTime: (state, time) => {
      let index = state.task.time_spent.indexOf(time)
      state.task.time_spent.splice(index, 1)
    },
    addFile: (state, file) => state.task.files.push(file),
    removeFileFromTask: (state, file) => {
      let index = state.task.files.indexOf(file)
      state.task.files.splice(index, 1)
    },
    sortTodos: (state, todos) => {
      state.todos = todos.map((todo, index) => {
        todo.sort = index
        return todo
      })
    }
  },

  actions: {
    loadTasks: ({commit}, payload) => new Promise((resolve, reject) => {
      pathLoader.loadPath(payload, ({data}) => {
        commit('setTasks', data)
        resolve()
      }, error => reject(error))
    }),
    loadTask: ({commit}, taskId) => new Promise((resolve, reject) => {
      backend.loadTask(taskId, ({data}) => {
        commit('setTask', data)
        resolve()
      }, error => reject(error))
    }),
    save: ({state}) => new Promise((resolve, reject) => {
      if (state.task.id) {
        backend.update(state.task, ({data}) => resolve(data), error => reject(error))
      } else {
        backend.store(state.task.payload, ({data}) => resolve(data), error => reject(error))
      }
    }),
    close: ({state, commit}) => new Promise((resolve, reject) => {
      backend.close(state.task.id, response => {
        commit('setStatus', response.data)
        resolve()
      }, error => reject(error))
    }),
    reopen: ({state, commit}) => new Promise((resolve, reject) => {
      backend.reopen(state.task.id, response => {
        commit('setStatus', response.data)
        resolve()
      }, error => reject(error))
    }),
    comment: ({state, commit}, comment) => new Promise((resolve, reject) => {
      backend.storeComment({
        taskId: state.task.id,
        comment
      }, ({data}) => {
        commit('addComment', data)
        resolve()
      }, error => reject(error))
    }),
    storeTodo: ({state, commit}, todo) => new Promise((resolve, reject) => {
      backend.storeTodo({
        taskId: state.task.id,
        todo
      }, ({data}) => {
        commit('addTodo', data)
        resolve()
      }, error => reject(error))
    }),
    updateComment: ({}, comment) => new Promise((resolve, reject) => {
      backend.updateComment(comment, ({}) => {
        resolve()
      }, error => reject(error))
    }),
    trashComment: ({}, comment) => new Promise((resolve, reject) => {
      backend.trashComment(comment, resolve, reject)
    }),
    assignUser: ({state, commit}, user) => new Promise((resolve, reject) => {
      backend.assignUser({
        taskId: state.task.id,
        userId: user.id
      }, ({}) => {
        commit('assignUser', user)
        resolve()
      }, error => reject(error))
    }),
    assignLabel: ({state, commit}, label) => new Promise((resolve, reject) => {
      backend.assignLabel({
        taskId: state.task.id,
        labelId: label.id
      }, ({}) => {
        commit('assignLabel', label)
        resolve()
      }, error => reject(error))
    }),
    unassignUser: ({state, commit}, user) => new Promise((resolve, reject) => {
      backend.unassignUser({
        taskId: state.task.id,
        userId: user.id
      }, ({}) => {
        commit('unassignUser', user)
        resolve()
      }, error => reject(error))
    }),
    removeLabel: ({state, commit}, label) => new Promise((resolve, reject) => {
      backend.removeLabel({
        taskId: state.task.id,
        labelId: label.id
      }, ({}) => {
        commit('removeLabel', label)
        resolve()
      }, error => reject(error))
    }),
    spendTime: ({state, commit}, hours) => new Promise((resolve, reject) => {
      backend.spendTime({
        hours,
        taskId: state.task.id
      }, ({data}) => {
        commit('spendTime', data)
        resolve()
      }, error => reject(error))
    }),
    updatePriority: ({state}, priority) => new Promise((resolve, reject) => {
      backend.updatePriority({
        taskId: state.task.id,
        priority_id: priority
      }, () => {
        resolve()
      }, error => reject(error))
    }),
    updateEstimatedTime: ({ state }) => new Promise((resolve, reject) => {
      backend.updateEstimatedTime({
        taskId: state.task.id,
        estimated_time: state.task.estimated_time
      }, () => {
        resolve()
      }, error => reject(error))
    }),
    toggleTodoComplete: ({commit}, todoId) => new Promise((resolve, reject) => {
      backend.toggleTodoComplete(todoId, () => {
        commit('toggleTodoComplete', todoId)
        resolve()
      }, error => reject(error))
    }),
    removeTodo: ({commit}, todoId) => new Promise((resolve, reject) => {
      backend.removeTodo(todoId, () => {
        commit('removeTodo', todoId)
        resolve()
      }, error => reject(error))
    }),
    removeTime: ({commit}, time) => new Promise((resolve, reject) => {
      backend.removeTime(time.id, () => {
        commit('removeTime', time)
        resolve()
      }, error => reject(error))
    }),
    upload: ({state, commit}, upload) => new Promise((resolve, reject) => {
      backend.uploadFile({
        taskId: state.task.id,
        upload
      }, ({data}) => {
        commit('addFile', data)
        resolve()
      }, error => reject(error))
    }),
    updateTodoContent: ({ commit }, todo) => new Promise((resolve, reject) => {
      backend.updateTodoContent(todo, response => {
        commit('updateTodoContent', response.data)
        resolve()
      }, reject)
    }),
    deleteFile: ({ commit }, file) => new Promise((resolve, reject) => {
      backend.deleteFile(file.id, () => {
        commit('removeFileFromTask', file)
        resolve()
      }, reject)
    }),
    updateTodosSort: ({state}, todos) => new Promise((resolve, reject) => {
      backend.updateTodosSort({
        taskId: state.task.id,
        todos
      }, resolve, reject)
    }),
    updateMilestone: ({state}, milestoneId) => new Promise((resolve, reject) => {
      backend.updateMilestone({
        taskId: state.task.id,
        milestone_id: milestoneId
      }, resolve, reject)
    }),
    clearMilestone: ({}, taskId) => new Promise((resolve, reject) => {
      backend.clearMilestone(taskId, resolve, reject)
    })
  },

  getters: {
    tasks: state => state.tasks,
    task: state => state.task,
    todos: state => sortBy(state.task.todos, todo => todo.sort),
    openTodos: ({}, getters) => getters.todos.filter(todo => !todo.completed),
    completedTodos: ({}, getters) => getters.todos.filter(todo => todo.completed),
  }
}
